import { Formik, Form, Field } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import DatePickerField from '../../Common/components/DatePickerField';
import LocationField from '../../Common/components/LocationField';
import useEditActivation from './hooks/useEditActivation';
import { useEffect, useState } from 'react';
import useGetActivation from './hooks/useActivation';
import Loading from '../../Common/components/Loading';
import { Button } from '@/src/shadComponents/ui/button';
import { Label } from '@/src/shadComponents/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/src/shadComponents/ui/card';
import PageShell from '@/src/Common/components/PageShell';

interface ActivationObject {
    id: number;
    name: string;
    date: Date;
    location: string;
    owner: string;
    isArchived: boolean;
    v3Folder: string;
}

export default function EditActivation() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [activation, setActivation] = useState<ActivationObject | null>(null);
    const { id } = useParams();
    const { getActivation } = useGetActivation();
    const { saveActivation } = useEditActivation(id ? +id : 0);

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            try {
                const activation = await getActivation(id ? +id : 0);
                setActivation(activation);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return <Loading isLoading={true} />;
    }

    if (!activation) {
        return <div></div>;
    }

    return (
        <PageShell
            title="Edit Activation"
            breadcrumbs={[
                { label: 'Activations', href: '/activations' },
                { label: 'Edit' },
            ]}
        >
            <Card>
                <CardContent className="pt-6">
                    <Formik
                        initialValues={activation}
                        onSubmit={async (values, actions) => {
                            const wasSuccessful = await saveActivation(values);
                            actions.setSubmitting(false);

                            if (wasSuccessful) {
                                navigate(-1);
                            }
                        }}
                    >
                        {(props) => (
                            <Form
                                className="space-y-6"
                                placeholder={undefined}
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="name">Name</Label>
                                    <Field
                                        id="name"
                                        name="name"
                                        placeholder="Activation Name"
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="date">Date</Label>
                                    <DatePickerField id="date" name="date" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="location">Location</Label>
                                    <LocationField name="location" id="location" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="v3Folder">V3 Folder</Label>
                                    <Field
                                        id="v3Folder"
                                        name="v3Folder"
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:max-w-md"
                                        placeholder="v3 folder name"
                                    />
                                </div>
                                <div className="flex items-center gap-4 pt-4">
                                    <Button type="submit">
                                        Save Activation
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate(-1)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Card>
        </PageShell>
    );
}
